<template>
  <div>
    <px-view-header title="Importar cotação" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>

    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>

    <v-stepper class="elevation-1 rounded-card" v-if="!loadingForm" v-model="currentStep" vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Arquivo
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-importar-cotacao-arquivo @next="onNextArquivo" v-if="cotacao"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2">
        Dados básicos
      </v-stepper-step>
      <v-stepper-content step="2">
        <px-cadastro-cotacao-dados-basicos :cotacao="cotacao"
                                           :campos-readonly="camposReadonly"
                                           :campos-obrigatorios="camposObrigatorios"
                                           @ready="next"
                                           @cancel="onClickCancelar"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3">Produtos</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-cotacao-produtos-table :cotacao="cotacao"
                                            :campos-readonly="camposReadonly"
                                            :campos-obrigatorios="camposObrigatorios"
                                            :erp-campos-integracao="erpCamposIntegracao"
                                            @next="next"
                                            @previous="previous"
                                            @resetfornecedor="resetFornecedor"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 4" step="4">Fornecedores</v-stepper-step>
      <v-stepper-content step="4">
        <px-cadastro-cotacao-fornecedores v-if="currentStep === 4"
                                          :cotacao="cotacao"
                                          :loading="loading"
                                          :is-editing="isRascunho"
                                          :show-rascunho="false"
                                          @previous="previous"
                                          @enviar-cotacao="enviarCotacao"
                                          @reset-fornecedor="resetFornecedor"/>
      </v-stepper-content>
    </v-stepper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { SituacaoCotacao } from 'px-business-components';
import ImportarCotacaoArquivo from '@/modules/cotacao/importacao/ImportarCotacaoArquivo.vue';
import pxCadastroCotacaoDadosBasicos
  from '@/modules/cotacao/cadastro/CadastroCotacaoDadosBasicos.vue';
import pxCadastroCotacaoProdutosTable
  from '@/modules/cotacao/cadastro/produtos/CadastroCotacaoProdutosTable.vue';
import pxCadastroCotacaoFornecedores
  from '@/modules/cotacao/cadastro/fornecedores/CadastroCotacaoFornecedores.vue';
import CotacaoService from '@/modules/cotacao/cotacao-service';
import CompradorService from '@/api/comprador-service';
import EntidadeCamposCotacaoService from '@/modules/erp-campos/entidade-campos-cotacao-service';
import { buildCadastrosAdicionaisPayload } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import { initialState, initialStateProduto } from '../cadastro/utils/cotacao-utils';

export default {
  data() {
    return {
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Cotações',
          to: '/cotacoes',
        },
        { text: 'Importar cotação' },
      ],
      currentStep: 1,
      loadingForm: false,
      loading: false,
      cotacao: null,
      camposReadonly: null,
      camposObrigatorios: null,
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao']),
  },
  methods: {
    onClickBack() {
      this.$router.push({ name: 'main.cotacoes' });
    },
    onNextArquivo(arquivoResult) {
      this.cotacao.produtos = arquivoResult.produtos.map(produto => ({
        ...initialStateProduto(this.cotacao, this.erpCamposIntegracao),
        especificacaoProduto: produto.especificacaoProduto,
        marca: produto.marca,
        produto: produto.produto,
        quantidade: produto.quantidade,
        unidadeMedida: produto.unidadeMedida,
        arquivos: produto.produto.arquivos || [],
        $$hasFileLoading: false,
      }));

      this.next();
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    onClickCancelar() {
      this.$router.push({ name: 'main.cotacoes' });
    },
    buildCotacaoBody() {
      const cotacao = { ...this.cotacao };
      cotacao.situacao = SituacaoCotacao.keys.ABERTA.key;
      cotacao.produtos = cotacao.produtos.map(produto => ({
        ...produto,
        situacao: SituacaoCotacao.keys.ABERTA.key,
        cadastrosAdicionais: buildCadastrosAdicionaisPayload(
          produto.cadastrosAdicionais,
          TiposEntidadeCadastroAdicional.keys.COTACAO_PRODUTO.key,
        ),
      }));
      return cotacao;
    },
    resetFornecedor() {
      this.cotacao.fornecedores = [];
    },
    enviarCotacao() {
      this.loading = true;
      CotacaoService.save(this.buildCotacaoBody())
        .then(() => {
          this.$toast('Cotação enviada com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCompradorFaturamentoDefault() {
      return CompradorService.getDefaultFaturamento()
        .then((response) => {
          this.cotacao.comprador = response.data;
        });
    },
    getCompradorEntregaDefault() {
      return CompradorService.getDefaultEntrega()
        .then((response) => {
          this.cotacao.locaisEntrega = response.data;
        });
    },
    getCamposReadonly() {
      EntidadeCamposCotacaoService.getCamposReadonly(this.erpCamposIntegracao)
        .then((data) => {
          this.camposReadonly = data;
        });
    },
    getCamposObrigatorios() {
      return EntidadeCamposCotacaoService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
  },
  created() {
    this.cotacao = initialState(this.erpCamposIntegracao);
    this.loadingForm = true;
    Promise.all([
      this.getCompradorEntregaDefault(),
      this.getCompradorFaturamentoDefault(),
      this.getCamposObrigatorios(),
      this.getCamposReadonly(),
    ])
      .finally(() => {
        this.loadingForm = false;
      });
  },
  components: {
    pxCadastroCotacaoFornecedores,
    pxCadastroCotacaoProdutosTable,
    pxCadastroCotacaoDadosBasicos,
    pxImportarCotacaoArquivo: ImportarCotacaoArquivo,
  },
};
</script>
